<template>
  <div class="for-creator">
    <beautiful-chat
      :key="componentKey"
      class="shadow overflow-hidden"
      :style="chatStyle"
      :title="title"
      :participants="participants"
      :title-image-url="titleImageUrl"
      :on-message-was-sent="onMessageWasSent"
      :new-messages-count="newMessagesCount"
      :message-list="messageList"
      :is-open="isChatOpen"
      :close="closeChat"
      :open="openChat"
      :show-emoji="false"
      :show-file="true"
      :show-edition="true"
      :show-deletion="false"
      :show-typing-indicator="showTypingIndicator"
      :disable-user-list-toggle="true"
      :show-launcher="false"
      :show-close-button="false"
      :show-user-list="false"
      :colors="colors"
      :always-scroll-to-bottom="true"
      :message-styling="true"
      :placeholder="placeholder"
      @edit="editMessage"
    >
      <template #system-message-body="{ message }">
        <div class="flex flex-col">
          <div v-html="message.text"></div>
          <a v-if="message.file && message.file.url" :href="message.file.url" target="_blank">
            <img :src="message.file.url" class="mx-auto block system-chat-image" />
          </a>
          <p v-if="message.meta" class="mt-1 text-center" style="font-size: xx-small">{{ message.meta }}</p>
        </div>
      </template>
    </beautiful-chat>
  </div>
</template>

<script>
import Echo from 'laravel-echo';
// eslint-disable-next-line no-unused-vars
import Pusher from 'pusher-js';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import { Bus } from '../../bus';

export default {
  props: {
    initialCampaignApplication: { type: Object, default: () => {} },
  },
  data: () => ({
    onlineUsers: [],
    participants: [],
    titleImageUrl: '/favicons/apple-touch-icon.png',
    rawMessageList: [],
    isChatOpen: false,
    showTypingIndicator: '',
    colors: {
      header: {
        bg: '#813571',
        text: '#ffffff',
      },
      launcher: {
        bg: '#813571',
      },
      messageList: {
        bg: '#fffafa',
      },
      sentMessage: {
        bg: '#813571',
        text: '#ffffff',
      },
      receivedMessage: {
        bg: '#eaeaea',
        text: '#222222',
      },
      userInput: {
        bg: '#fdedea',
        text: '#565867',
      },
    },
    newMessagesCount: 0,
    user: {},
    Echo: {},
    initializedWS: false,
    componentKey: 0,
    campaignApplication: {},
    markAsUnreadClicked: false,
  }),
  computed: {
    title() {
      return 'Chat with us';
    },
    messageList() {
      let idxLastContract;

      return uniqBy(
        this.rawMessageList
          .map((message, idx) => {
            if (message.type === 'system' && message.data.text && message.data.text.includes("class='contract'")) {
              idxLastContract = idx;
            }

            message.author = 'me';

            return message;
          })
          .map((message, idx) => {
            if (
              idx !== idxLastContract &&
              message.type === 'system' &&
              message.data.text.includes("class='contract'")
            ) {
              message.data.text = message.data.text.replace(/<a class='contract'.*<\/a>/g, 'collaboration terms');
            }

            return message;
          }),
        'id',
      );
    },
    userId() {
      return this.user.id;
    },
    creatorIsOnline() {
      return this.onlineCreators.some((u) => u.id !== 1);
    },
    channelName() {
      return `campaignApplication.${this.campaignApplication.id}`;
    },
    onlineCreators() {
      return this.onlineUsers.filter((u) => u.id !== this.user.id);
    },
    participantCreators() {
      return this.participants.filter((u) => u.id !== 1);
    },
    creator() {
      if (this.participantCreators.length > 0) return this.participantCreators[0];

      return null;
    },
    creatorName() {
      if (!this.creator) return 'the creator';

      return this.creator.name;
    },
    creatorAvatar() {
      if (!this.creator) return null;

      return this.creator.imageUrl;
    },
    joined() {
      return (
        !isEmpty(this.campaignApplication) &&
        !['pending_approval', 'rejected_approval'].includes(this.campaignApplication?.status)
      );
    },
    pendingContract() {
      return this.campaignApplication?.status === 'pending_contract';
    },
    negotiating() {
      return this.campaignApplication?.status === 'negotiating';
    },
    cancelled() {
      return this.campaignApplication?.status === 'cancelled';
    },
    isChatEnabled() {
      return this.joined || this.pendingContract || this.negotiating;
    },
    placeholder() {
      return this.isChatEnabled && !this.cancelled
        ? 'Write a message...'
        : 'The chat will be enabled once you join the campaign.';
    },
    chatStyle() {
      return this.isChatEnabled ? '' : 'pointer-events: none';
    },
    pusherCredentials() {
      return window.Creators.pusherCredentials;
    },
  },
  async mounted() {
    this.campaignApplication = this.initialCampaignApplication;

    this.openChat();

    Bus.$on('joinedCampaign', this.forceRerender);
  },
  updated() {
    document.querySelectorAll('.sc-image:not(.hidden)').forEach((img) => {
      img.onerror = function () {
        this.classList.add('hidden');
      };
    });
  },
  async destroyed() {
    await this.Echo.leave(this.channelName);
    await this.Echo.disconnect();
  },
  methods: {
    async markAsUnread() {
      await window.axios.post(`/campaign-applications/${this.campaignApplication.id}/mark-messenger-unread`);
      this.markAsUnreadClicked = true;
    },
    async markAsRead() {
      await window.axios.post(`/campaign-applications/${this.campaignApplication.id}/mark-messenger-read`);
    },
    async postMessage(message) {
      if (this?.campaignApplication?.status === 'rejected_approval') {
        this.$toasted.show('You cannot send messages after being rejected from the campaign.', { type: 'error' });

        return;
      }

      if (this?.campaignApplication?.status === 'pending_approval') {
        this.$toasted.show('You cannot send messages before being accepted to the campaign.', { type: 'error' });

        return;
      }

      if (this.cancelled) {
        this.$toasted.show('You cannot send messages after your collaboration has been cancelled.', { type: 'error' });

        return;
      }

      const formData = new FormData();
      formData.append('message', JSON.stringify(message));
      if (message.type === 'file') {
        formData.append('messageFile', message.data.file);
      }

      try {
        const { data } = await window.axios.post(
          `/threads/campaign-applications/${this.campaignApplication.id}/messages`,
          formData,
        );
        const { success, message_id: messageId, file } = data;
        if (success) {
          if (messageId) {
            this.updateMessageId(message.hash, messageId);
          }
          if (message.type === 'file') {
            this.updateMessageFile(messageId, file);
          }
        } else {
          this.removeLastMessage();
          this.$toasted.show('The message could not be sent.', { type: 'error' });
        }
      } catch (err) {
        this.removeLastMessage();

        this.$toasted.show(err, { type: 'error' });
      }
    },
    removeLastMessage() {
      this.rawMessageList.pop();
    },
    updateMessageId(hash, id) {
      this.rawMessageList = this.rawMessageList.map((message) => {
        if (message.hash === hash) {
          const newMessage = {
            ...message,
            id,
          };

          return newMessage;
        }

        return message;
      });
    },
    updateMessageFile(id, file) {
      this.rawMessageList = this.rawMessageList.map((message) => {
        if (message.id === id) {
          message.data.file = file;

          return message;
        }

        return message;
      });
    },
    randomString(length) {
      return Math.round(36 ** length + 1 - Math.random() * 36 ** length)
        .toString(36)
        .slice(1);
    },
    onMessageWasSent(message) {
      const newMessage = {
        ...message,
        // eslint-disable-next-line camelcase
        sent_from_support: false,
        hash: this.randomString(16),
      };
      // called when the user sends a message
      this.postMessage(newMessage);
      // we can comment the next line to avoid the flicking
      this.rawMessageList = [...this.rawMessageList, newMessage];
    },
    async editMessage(message) {
      const m = this.messageList.find((m) => m.id === message.id);
      m.data.text = message.data.text;

      const { data } = await window.axios.put(`/threads/messages/${message.id}`, { message });
    },
    async openChat() {
      this.isChatOpen = true;

      if (this.isChatEnabled && this.rawMessageList.length === 0) {
        await this.getThread();
      }

      if (!this.initializedWS && this.joined) {
        this.initWS();
      }
    },
    closeChat() {
      this.isChatOpen = false;
    },
    async getThread() {
      const { data } = await window.axios.get(`/threads/campaign-applications/${this.campaignApplication.id}/messages`);

      const { messages, participants, user } = data;
      this.rawMessageList = [...messages];
      this.participants = participants;
      this.user = user;
    },
    initWS() {
      this.Echo = new Echo({
        broadcaster: 'pusher',
        key: this.pusherCredentials.key,
        cluster: this.pusherCredentials.cluster,
        encrypted: true,
      });

      this.Echo.join(this.channelName)
        .here((users) => {
          this.onlineUsers = users;
        })
        .joining((user) => {
          this.userJoined(user);
        })
        .leaving((user) => {
          this.userLeft(user);
        })
        .listen('MessageStored', (event) => {
          const { message } = event;

          if (message && !this.messagePresent(message.id)) {
            this.rawMessageList = [...this.rawMessageList, message];
            Bus.$emit('newMessage');

            this.newMessagesCount = 1;
          }
        });

      this.initializedWS = true;
    },
    messagePresent(id) {
      return this.rawMessageList.some((message) => message.id === id);
    },
    userJoined(user) {
      this.onlineUsers = [...this.onlineUsers, user];
    },
    userLeft(user) {
      this.onlineUsers = this.onlineUsers.filter((participant) => participant.id !== user.id);
    },
    forceRerender({ campaignApplication }) {
      if (campaignApplication !== undefined) {
        this.campaignApplication = campaignApplication;
      }

      if (this.joined) {
        this.getThread();
        this.$forceUpdate();
        this.componentKey += 1;
      }
    },
  },
};
</script>

<style lang="scss">
.for-creator {
  .sc-launcher {
    display: none !important;
  }
  .sc-chat-window {
    border-radius: 0 !important;
    position: relative !important;
    width: 100% !important;
    margin: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    height: 700px !important;
  }
  .sc-message {
    width: calc(100% - 2rem) !important;
  }
  .sc-header {
    border-radius: 0;
  }
  .sc-user-input {
    border-radius: 0;
  }
}
.sc-header--img {
  height: 52px;
  width: 52px;
}
.chatLink-url {
  color: #0390fc;
}
.file-container * {
  height: 15px !important;
}
.tooltip .tooltip-inner {
  background: white;
  color: black;
}
.system-chat-image {
  height: 30%;
  width: 30%;
}
.sc-message--system {
  background-color: #f3ecf2 !important;
  color: #222222 !important;
  opacity: 0.7 !important;
}
</style>
